import { AfterViewInit, Component, DoCheck, EventEmitter, Input, OnChanges, Output, SecurityContext, SimpleChanges } from "@angular/core";
import { ControlContainer, FormControl, NgModelGroup } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { FileService } from "@tylertech/core-module";
import { Utilities } from "@tylertech/shared-module";
import { IFile, PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataPropertyService, MetadataService } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";
class BadFile
{
    FileName: string;

    Message: string;
}
@Component({
    selector: "metadata-input-file",
    templateUrl: "./metadata-input-file.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
// @LogComponentChanges
export class MetadataInputFileComponent implements AfterViewInit, OnChanges, DoCheck
{
    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: IFile[] | IFile;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    compact: boolean;

    @Input()
    label: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    /** Accessible in the template */ isViewOnly: boolean;

    /** Accessible in the template */ documentUrl: SafeResourceUrl;

    private _valueModel: IFile[] | IFile;

    private showTypeField: boolean = true;

    private filePickerElement: HTMLElement;

    private _maxFileSize: string = "52428800"; // = 50MB

    private _badFileList: BadFile[] = [];

    private _isDirty: boolean = false;

    private initialized: boolean = false;

    private _showDocument: boolean = false;
    url: string;

    constructor(
        public modelGroup: NgModelGroup,
        private readonly $fileService: FileService,
        private readonly $metadataService: MetadataService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private readonly domSanitizer: DomSanitizer
    ) { }

    ngDoCheck(): void
    {
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
    }

    ngAfterViewInit(): void
    {
        if (this.initialized)
            return;
        this.initialized = true;
        this.modelGroup.control.addControl(this.Name, new FormControl());
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        {
            const changedKeys = Object.keys(changesObj);
            for (const key of changedKeys)
            {
                switch (key)
                {
                    case "model":
                        const change = changesObj[key];
                        if (!change.currentValue)
                        {
                            this.valueModel = null;
                        }
                        else if (!this.valueModel || this.valueModel !== change.currentValue)
                        {
                            if (Array.isArray(change.currentValue))
                            {
                                const values = [];
                                this.valueModel = [];
                                change.currentValue.forEach(val =>
                                {
                                    values.push(MetadataInputFileComponent.toFileObject(val));
                                });
                                this.valueModel = values;
                            }
                            else
                            {
                                this.valueModel = MetadataInputFileComponent.toFileObject(change.currentValue);
                            }
                        }
                        else if (this.valueModel && this.isShowable && this.propertyMetadata.DefaultShow)
                        {
                            this.show();
                        }
                }
                break;
            }
        }
    }

    get valueModel(): IFile[] | IFile
    {
        if (!this._valueModel)
        {
            if (Array.isArray(this._valueModel))
            {
                const values = [];
                for (const val in this.model as any)
                {
                    values.push(MetadataInputFileComponent.toFileObject(this.model[val]));
                }
                this._valueModel = values;
            }
            else
            {
                this._valueModel = MetadataInputFileComponent.toFileObject(this.model as IFile);
            }
        }
        return this._valueModel;
    }

    set valueModel(value: IFile[] | IFile)
    {
        this._valueModel = value;
    }

    get badFileList(): BadFile[]
    {
        return this._badFileList;
    }

    set badFileList(value: BadFile[])
    {
        this._badFileList = value;
    }

    get isRequired(): boolean
    {
        return this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) && !this.isViewOnly;
    }

    get telemetryLabel(): string
    {
        return "Submission-" + this.label;
    }

    get isInvalid(): boolean
    {
        const target_copy = Object.assign({}, this.metadataModel);
        return (
            this.isRequired &&
            !this.filesExist &&
            !Utilities.IsNullOrUndefined(target_copy.$errors) &&
            !Utilities.IsNullOrUndefined(target_copy.$errors[this.Name])
        );
    }

    get horizontalMode(): boolean
    {
        return !this.allowMultiple && !this.showDragDrop;
    }

    get isShowDocumentMode(): boolean
    {
        return this._showDocument;
    }

    get isDownloadable(): boolean
    {
        return (
            this.filesExist &&
            (Utilities.IsNullOrUndefined(this.propertyMetadata.Downloadable)
                ? false
                : this.propertyMetadata.Downloadable)
        );
    }

    get isShowable(): boolean
    {
        return (
            this.filesExist &&
            !this.propertyMetadata.AllowMultipleFiles &&
            (Utilities.IsNullOrUndefined(this.propertyMetadata.Showable)
                ? false
                : this.propertyMetadata.Showable) &&
            this.isWhiteListedType
        );
    }

    get isWhiteListedType(): boolean
    {
        const file: IFile = <IFile>this.valueModel;
        const extension = file.FileName.substring(file.FileName.lastIndexOf("."), file.FileName.length);
        const whiteListedTypes = [".png", ".jpg", ".pdf"];
        return whiteListedTypes.includes(extension);
    }

    get allowMultiple(): boolean
    {
        return this.propertyMetadata.AllowMultipleFiles;
    }

    get showDragDrop(): boolean
    {
        return this.propertyMetadata.ShowDragAndDrop;
    }

    get filesExist(): boolean
    {
        if (Array.isArray(this.valueModel))
        {
            return !!this.valueModel && !!this.valueModel.find(x => x.Contents !== null && x.Contents !== undefined);
        }
        else
        {
            return !!this.valueModel && !!this.valueModel.Contents;
        }
    }

    get buttonMessage(): string
    {
        return this.allowMultiple ? "Select files" : "Select file";
    }

    get primaryMessage(): string
    {
        return this.allowMultiple ? "Drag files here or" : "Drag file here or";
    }

    get allowedTypes(): string
    {
        const defaultTypes = ".txt, .csv, .xls, .xlsx, .pdf";
        if (Utilities.IsNullOrUndefined(this.propertyMetadata.AllowedTypes)) return defaultTypes.replace(/\s/g, "");
        if (this.propertyMetadata.AllowedTypes === "*")
        {
            this.showTypeField = false;
            return ".*";
        }
        return this.propertyMetadata.AllowedTypes.replace(/\s/g, "");
    }

    get allowedTypesView(): string
    {
        return this.propertyMetadata.AllowedTypes === "*" ? "" : this.propertyMetadata.AllowedTypes;
    }

    get customFormName(): string
    {
        return `metadataInputFileForm_${ObjectHelpers.stripInvalidChars(this.propertyMetadata.Name)}`;
    }

    get maxSize(): string
    {
        return this._maxFileSize;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    private static toFileObject(value: string | IFile): IFile
    {
        return typeof value === "string" ? <IFile>{ FileName: value } : value;
    }

    get formatMaxSize(): string
    {
        const bytes = parseInt(this.maxSize);
        let size = "";
        if (bytes >= 1073741824)
        {
            size = (bytes / 1073741824).toFixed(0) + " GB";
        }
        else if (bytes >= 1048576)
        {
            size = (bytes / 1048576).toFixed(0) + " MB";
        }
        else if (bytes >= 1024)
        {
            size = (bytes / 1024).toFixed(0) + " KB";
        }
        else if (bytes > 1)
        {
            size = bytes + " bytes";
        }
        else if (bytes === 1)
        {
            size = bytes + " byte";
        }
        else
        {
            size = "0 bytes";
        }
        return size;
    }

    onFileSelected($event): void
    {
        const self = this;
        const htmlInputFile: any = $event.detail;
        this._isDirty = true;
        if (htmlInputFile.illegalFiles && htmlInputFile.illegalFiles.length >= 1)
        {
            const badFiles = [];
            htmlInputFile.illegalFiles.forEach((file: File) =>
            {
                let message = "";
                if (file.size > parseInt(this.maxSize))
                {
                    message = "File size is too large";
                    this.modelGroup.control.controls[this.Name].setErrors({ "File size is too large": true });
                }
                else if (!this.validateFileType(file))
                {
                    message = "Invalid file type";
                    this.modelGroup.control.controls[this.Name].setErrors({ "Invalid file type": true });
                }
                badFiles.push({
                    FileName: file.name,
                    Message: message,
                });
            });
            this.badFileList = badFiles;
        }
        else
        {
            this.badFileList = [];
        }
        if (!htmlInputFile.legalFiles || htmlInputFile.legalFiles.length < 1)
        {
            return;
        }

        this.modelGroup.control.controls[this.Name].setErrors(null);
        let readers = [];
        htmlInputFile.legalFiles.forEach(file =>
        {
            readers.push(getFiles(file));
        });

        Promise.all(readers).then((values) =>
        {
            if (self.metadataModel?.$errors)
                delete self.metadataModel.$errors[self.Name];
            this.fireValueChanged(this.valueModel);
            // componentContext.fireValueChanged(componentContext.valueModel);
        });

        function getFiles(file: File): any
        {
            return new Promise(function (resolve, reject)
            {
                const reader = new FileReader();
                reader.onload = function ()
                {
                    const uInt8Array = new Uint8Array(reader.result as ArrayBuffer);
                    let charCode = "";
                    //Avoiding maximum stack depth
                    for (let i = 0; i < uInt8Array.length; ++i)
                    {
                        charCode += String.fromCharCode(uInt8Array[i]);
                    }

                    const data = btoa(charCode);
                    const newFile: IFile = {
                        Contents: data,
                        FileName: file.name,
                        Type: file.type
                    };
                    if (self.allowMultiple)
                    {
                        if (Utilities.IsNullOrUndefined(self.valueModel))
                        {
                            self.valueModel = [];
                        }
                        (<IFile[]>self.valueModel).push(newFile);
                    }
                    else
                    {
                        self.valueModel = newFile;
                    }
                    resolve(self.valueModel);
                };
                reader.readAsArrayBuffer(file);
            });

        }
    }

    validateFileType(file: File): boolean
    {
        const extension = file.name.substring(file.name.lastIndexOf("."), file.name.length);
        return this.allowedTypes.includes(extension);
    }

    fireValueChanged(newValue: IFile[] | IFile): void
    {
        if (this.propertyMetadata.DefaultShow)
        {
            this.show();
        }

        if (this.model === newValue || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: newValue });
    }

    download(): void
    {
        if (Array.isArray(this.valueModel))
        {
            this.valueModel.forEach(file =>
            {
                this.$fileService.openDocumentLink(file.Contents, file.FileName, file.FileName);
            });
        }
        else
        {
            this.$fileService.openDocumentLink(
                this.valueModel.Contents,
                this.valueModel.FileName,
                this.valueModel.FileName
            );
        }
    }

    show(): void
    {
        const file = this.valueModel as IFile;
        const blob = this.$fileService.b64toBlob(file.Contents, file.Type, true);
        this.url = URL.createObjectURL(blob);
        const url = this.domSanitizer.sanitize(SecurityContext.URL, this.url);
        this.documentUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
        //this.documentUrl = this.$sce.trustAsResourceUrl(this.url);

        this._showDocument = true;
    }

    hide(): void
    {
        this._showDocument = false;
        URL.revokeObjectURL(this.url);
    }

    clear(file): void
    {
        if (!Utilities.IsNullOrUndefined(file) && Array.isArray(this.valueModel))
        {
            const index = this.valueModel.indexOf(file);
            if (index !== -1)
            {
                this.valueModel.splice(index, 1);
            }
            this.fireValueChanged(this.valueModel);
        }
        else
        {
            this.fireValueChanged(null);
        }
    }
}
